import * as Cesium from 'cesium'

export const DataSourceCreation = {
  methods: {
    createDataSource() {
      const dataSource = this.initDataSource()
      this.initTrace(dataSource)
      this.initGroundStations(dataSource)
      this.initSunDir(dataSource)
      return dataSource
    },

    initDataSource() {
      let dataSource = new Cesium.CustomDataSource('CustomDataSource')
      let clock = new Cesium.DataSourceClock()
      clock.clockRange = Cesium.ClockRange.LOOP_STOP
      clock.startTime = Cesium.JulianDate.fromDate(this.initialDate)
      clock.stopTime = Cesium.JulianDate.fromDate(this.finalDate)
      clock.currentTime = Cesium.JulianDate.fromDate(this.initialDate)
      clock.multiplier = 30
      dataSource.clock = clock
      return dataSource
    },

    createSampledProperties() {
      let spacecraftPositions = new Cesium.SampledPositionProperty(
        Cesium.ReferenceFrame.INERTIAL,
        0
      )
      spacecraftPositions.addSamplesPackedArray(
        this.cartesianData,
        Cesium.JulianDate.fromDate(this.initialDate)
      )

      let spacecraftOrientations = new Cesium.SampledProperty(
        Cesium.Quaternion,
        0
      )

      spacecraftOrientations.addSamplesPackedArray(
        this.sampledQuaternions,
        Cesium.JulianDate.fromDate(this.initialDate)
      )
      return [spacecraftPositions, spacecraftOrientations]
    },

    getAuthToken() {
      let authToken = ''
      document.cookie.split(';').forEach((cookie) => {
        if (cookie.includes('bearer')) {
          authToken = cookie.replace('bearer=', 'Bearer ')
        }
      })
      return authToken
    },

    initTrace(datasource) {
      const authToken = this.getAuthToken()
      const [positionProperty, orientationProperty] =
        this.createSampledProperties()

      datasource.entities.add(
        new Cesium.Entity({
          id: this.currentMission?.spacecraft?.name
            ? this.currentMission.spacecraft.name.replace(' ', '_')
            : 'spacecraft',
          name: this.currentMission?.spacecraft?.name
            ? this.currentMission.spacecraft.name
            : 'Mission Spacecraft',
          description: this.currentMission.spacecraft
            ? this.tableFromJson(this.currentMission.spacecraft).outerHTML
            : '<p>spacecraft</p>',
          position: positionProperty,
          orientation: orientationProperty,
          model: {
            uri: new Cesium.Resource({
              url: `/exoops/users/${this.currentUser.id}/getthreedmodel/${this.currentMission.spacecraft.spacecraftGeometry.id}`,
              headers: {
                Authorization: authToken
              }
            }),
            scale: 10,
            maximumScale: 2e6,
            distanceDisplayCondition: Cesium.DistanceDisplayCondition(0, 2e7)
          },
          label: {
            text: this.currentMission.spacecraft.name,
            font: '24px Helvetica',
            fillColor: Cesium.Color.WHITE,
            outlineColor: new Cesium.Color(150 / 255, 0, 150 / 255, 1),
            outlineWidth: 3,
            pixelOffset: new Cesium.Cartesian2(20, 20),
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
              5e4,
              8e7
            )
          },
          point: {
            pixelSize: 8,
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 1,
            color: new Cesium.Color(37 / 255, 140 / 255, 200 / 255, 1),
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
              2e7,
              5e9
            )
          },
          path: {
            material: new Cesium.PolylineOutlineMaterialProperty({
              color: new Cesium.Color(1, 0, 1, 1),
              outlineColor: new Cesium.Color(0, 1, 1, 1),
              outlineWidth: 5
            }),
            width: 2,
            leadTime: 0,
            trailTime: 1e3,
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
              0,
              8e7
            )
          }
        })
      )
    },

    initReferencePoint(datasource, id, sampledPosArray, interpolDeg = 5) {
      let pointPositions = new Cesium.SampledPositionProperty(
        Cesium.ReferenceFrame.INERTIAL,
        0
      )
      pointPositions.addSamplesPackedArray(
        sampledPosArray,
        Cesium.JulianDate.fromDate(this.initialDate)
      )

      pointPositions.setInterpolationOptions(
        Cesium.LagrangePolynomialApproximation,
        interpolDeg
      )
      return datasource.entities.add(
        new Cesium.Entity({
          id: id,
          name: 'id',
          position: pointPositions
        })
      )
    },

    initAxis(datasource, name, sampledPosArray, rgbaColor, interpolDeg = 5) {
      const refPoint = this.initReferencePoint(
        datasource,
        name + '_referencePoint',
        sampledPosArray,
        interpolDeg
      )

      datasource.entities.add(
        new Cesium.Entity({
          name: name,
          polyline: {
            positions: new Cesium.PositionPropertyArray([
              new Cesium.ReferenceProperty(
                datasource.entities,
                this.currentMission.spacecraft.name.replace(' ', '_'),
                ['position']
              ),
              new Cesium.ReferenceProperty(datasource.entities, refPoint.id, [
                'position'
              ])
            ]),
            material: new Cesium.PolylineArrowMaterialProperty(
              new Cesium.Color(
                rgbaColor[0] / 255,
                rgbaColor[1] / 255,
                rgbaColor[2] / 255,
                rgbaColor[3] / 255
              )
            ),
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
              0,
              1e5
            ),
            width: 8,
            arcType: Cesium.ArcType.NONE
          }
        })
      )
    },

    initSunDir(datasource) {
      this.initAxis(
        datasource,
        'Sun Polyline invisibleREF',
        this.sampledSunDir,
        [255, 255, 0, 255],
        3
      )
    },

    initGroundStations(datasource) {
      if (
        !this.currentMission.spacecraft.groundStations ||
        !this.currentMission.spacecraft.groundStations.length
      ) {
        return
      }
      this.currentMission.spacecraft.groundStations.forEach((gs) => {
        datasource.entities.add(
          new Cesium.Entity({
            id: gs.name.replace(' ', '_') + '_GroundStation',
            name: gs.name,
            position: new Cesium.ConstantPositionProperty(
              Cesium.Cartesian3.fromDegrees(
                Cesium.Math.toDegrees(gs.longitude),
                Cesium.Math.toDegrees(gs.latitude),
                0,
                this.ellipsoid
              )
            ),
            point: {
              heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
              outlineColor: Cesium.Color.BLACK,
              outlineWidth: 1,
              color: new Cesium.Color(1, 50 / 255, 50 / 255, 1),
              pixelSize: 10
            },
            label: {
              outlineColor: Cesium.Color.BLACK,
              style: Cesium.LabelStyle.FILL_AND_OUTLINE,
              pixelOffset: new Cesium.Cartesian2(10, -10),
              horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
              text: gs.name
            }
          })
        )
      })
    },

    tableFromJson(data) {
      const jsonData = data
      let rows = []
      Object.keys(jsonData).forEach((key) => {
        if (
          (typeof jsonData[key] !== 'object' && jsonData[key] !== null) ||
          jsonData[key] !== undefined
        ) {
          rows.push([key, jsonData[key]])
        }
      })
      rows.splice(0, 2)

      // Create a table.
      const table = document.createElement('table')

      rows.forEach((row) => {
        let tr = table.insertRow(-1)
        row.forEach((el) => {
          const cell = tr.insertCell(-1)
          cell.innerHTML = el
        })
      })

      return table
    }
  }
}
